import type { Client, Operation, SSRData } from '@urql/core';
import type { UpdateResolver } from '@urql/exchange-graphcache';

import { createClient, fetchExchange, mapExchange, ssrExchange, subscriptionExchange } from '@urql/core';
import { devtoolsExchange } from '@urql/devtools';
import { cacheExchange } from '@urql/exchange-graphcache';
import { defineNuxtPlugin } from '#app';
import cookie from 'cookie';

import type {
  AwardExternalCoinsMutation,
  BulkDeclineStudentRoleRequestsInput,
  SchoolMutations,
  SchoolStudentRoleInviteFieldsFragment,
} from '~/graphql/generated/graphql';
import type { OperationGate } from '~/utils/operationGate';

import schema from '@/graphql/generated/introspection.json';
import { GET_ADMIN_COACH_SESSION } from '~/graphql/documents/coachReview';
import { getUserDetail } from '~/graphql/documents/content';
import { fetchReferralsData } from '~/graphql/documents/learn/referrals';
import { getSelfWallet } from '~/graphql/documents/wallet';
import {

  ExternalCoinAwardOutcome,
  RoleInviteStatus,
  SpotlightTargetType,

} from '~/graphql/generated/graphql';
import { createAdminMfaRequiredHandler, navigateToAdminMfaLogin } from '~/utils/adminMfaRoute';
import { graphcacheKeys } from '~/utils/graphcacheKeys';
import { createOperationGate } from '~/utils/operationGate';

const CACHE_KEY = '__URQL_DATA__';

const GQL_OP_STREAMING_SET = new Set<number>();

/**
 * A student's state drives both the coins their wallet items are worth and the `Occupation.localWage`
 * every job page and coach read shows, so both types are stale the moment the viewer's state changes —
 * whether the role moved state, or a guest who read a job page just became an authenticated student.
 * Those reads are cache-first and would otherwise serve the previous viewer's figures for the rest of
 * the session, because sign-in navigates in the SPA and the client keeps its cache.
 */
export const viewerScopedCacheUpdater: UpdateResolver = (_result, _args, cache) => {
  cache.invalidate('UserWalletItemValue');
  cache.invalidate('Occupation');
};

/**
 * Pressing Start on an anonymous lesson may mint a fresh participant, and nothing reloads, so the
 * next child on a classroom device would otherwise open the lesson on the last one's completions:
 * `currentUserProgress` is viewer-scoped but hangs off `LearningAsset`, which is keyed by its own id
 * and so is the same entity for everybody.
 */
export const invalidateViewerProgress: UpdateResolver = (_result, _args, cache) => {
  cache.invalidate('UserProgress');
};

/**
 * Re-targeting a lesson changes who it reaches, and its statistics are scoped to that audience
 * ("reporting mirrors access"). Those aggregates are not fields on the returned lesson entity, so
 * graphcache cannot refresh them from the mutation result the way it does the audience badge and the
 * group chips. Drop the Insights query and the Lesson Hub tile for this playlist so both refetch with
 * the new audience's population.
 */
export const invalidateLessonInsightsOnAudienceChange: UpdateResolver = (_result, args, cache) => {
  const playlistId = (<{ input?: { playlistId?: string } }>args)?.input?.playlistId;
  if (!playlistId) {
    return;
  }

  cache.inspectFields('Query').forEach((field) => {
    if (field.fieldName === 'teacherLessonStatistics' && field.arguments?.id === playlistId) {
      cache.invalidate('Query', field.fieldKey);
    }
  });

  cache.invalidate({ __typename: 'LearningAssetCollection', id: playlistId }, 'teacherStatisticsSummary');
};

/**
 * Neither list is a field on the asset the mutation returns, so graphcache cannot refresh them from
 * the result: the consumer catalogue reads `LearningAssetPaginator` and the admin content table
 * reads `AdminLearningAssetPaginator`, and both are cache-first.
 */
export const invalidateLearningAssetLists: UpdateResolver = (_result, _args, cache) => {
  cache.invalidate('LearningAssetPaginator');
  cache.invalidate('AdminLearningAssetPaginator');
};

/** Deleting an asset drops both normalised rows for it as well as the admin list it appeared in. */
export const invalidateDeletedLearningAsset: UpdateResolver = (_result, args, cache) => {
  const id = (<{ input: { id: string } }>args).input.id;
  cache.invalidate({ __typename: 'LearningAsset', id });
  cache.invalidate({ __typename: 'AdminLearningAsset', id });
  cache.invalidate('AdminLearningAssetPaginator');
};

/**
 * Collections are listed twice over: the admin content table reads
 * `AdminLearningAssetCollectionPaginator` and the consumer catalogue reads
 * `LearningAssetCollectionPaginator` cache-first, so an editor who saves or deletes a collection and
 * then opens /learn in the same session would be served the pre-edit page.
 */
export const invalidateLearningAssetCollectionLists: UpdateResolver = (_result, _args, cache) => {
  cache.invalidate('AdminLearningAssetCollectionPaginator');
  cache.invalidate('LearningAssetCollectionPaginator');
};

/**
 * Drop every cached `myLessonCollections` field key (all status variants) so the teacher's
 * Active/Drafts/Archived lists refetch. Shared by the lesson mutations that move a playlist
 * between buckets (generate / activate / archive / discard).
 */
export const invalidateMyLessonCollections: UpdateResolver = (_result, _args, cache) => {
  cache.inspectFields('Query').forEach((field) => {
    if (field.fieldName === 'myLessonCollections') {
      cache.invalidate('Query', field.fieldKey);
    }
  });
};

/**
 * The CMS table and the flat list behind the asset-table area filter and the parent pickers. The
 * list is always read with filter arguments, so each argument variant is its own field key and a
 * bare `invalidate('Query', 'considerationAreasList')` would miss them.
 */
export const invalidateConsiderationAreaLists: UpdateResolver = (_result, _args, cache) => {
  cache.invalidate('AdminConsiderationAreaPaginator');
  cache.inspectFields('Query').forEach((field) => {
    if (field.fieldName === 'considerationAreasList') {
      cache.invalidate('Query', field.fieldKey);
    }
  });
};

/**
 * The spotlight mutations return the spotlight alone, so graphcache has no link between their
 * payloads and the collection or asset that owns it. Drop just that field so the next query
 * refetches it — invalidating the whole owning entity would null everything else the editor is
 * holding.
 */
export const invalidateSpotlight: UpdateResolver = (_result, args, cache) => {
  const input = <{ targetType?: SpotlightTargetType; targetId?: string } | undefined>args.input;

  if (!input?.targetId) {
    return;
  }

  const typename = input.targetType === SpotlightTargetType.LearningAsset
    ? 'AdminLearningAsset'
    : 'AdminLearningAssetCollection';

  cache.invalidate({ __typename: typename, id: input.targetId }, 'spotlight');
};

/**
 * The teacher pickers read the frameworks a school's state may use as `curriculumFrameworksForSchool`,
 * one field key per school. The mutation returns an `AdminCurriculumFramework`, which never reaches
 * that list, so a states save would otherwise leave the pickers on the old set until a reload.
 */
export const invalidateSchoolCurriculumFrameworks: UpdateResolver = (_result, _args, cache) => {
  cache.inspectFields('Query').forEach((field) => {
    if (field.fieldName === 'curriculumFrameworksForSchool') {
      cache.invalidate('Query', field.fieldKey);
    }
  });
};

interface CachedCoachSessionAnnotation { id: string }

interface CachedCoachSession { annotations: CachedCoachSessionAnnotation[] }

/**
 * The transcript reads a session's notes off the session, a list graphcache cannot grow on its own
 * from a created entity, so the new note is appended to it here. A refresh that lands before the
 * create response can already carry the note, so it is appended only when the list lacks it.
 */
export const appendCreatedCoachSessionAnnotation: UpdateResolver = (result, _args, cache) => {
  const payload = (<{ createCoachSessionAnnotation?: { annotation: CachedCoachSessionAnnotation & { sessionId: string } } }>result)
    .createCoachSessionAnnotation;
  const created = payload?.annotation;
  if (!created) {
    return;
  }
  cache.updateQuery(
    { query: GET_ADMIN_COACH_SESSION, variables: { id: created.sessionId } },
    (data) => {
      if (!data?.adminCoachSession) {
        return data;
      }
      const session = <CachedCoachSession><unknown>data.adminCoachSession;
      if (!session.annotations.some(annotation => annotation.id === created.id)) {
        session.annotations = [...session.annotations, created];
      }
      return data;
    },
  );
};

export const removeDeletedCoachSessionAnnotation: UpdateResolver = (result, _args, cache) => {
  const deleted = (<{ deleteCoachSessionAnnotation?: { id: string; sessionId: string } }>result)
    .deleteCoachSessionAnnotation;
  if (!deleted) {
    return;
  }
  cache.updateQuery(
    { query: GET_ADMIN_COACH_SESSION, variables: { id: deleted.sessionId } },
    (data) => {
      if (!data?.adminCoachSession) {
        return data;
      }
      const session = <CachedCoachSession><unknown>data.adminCoachSession;
      session.annotations = session.annotations.filter(annotation => annotation.id !== deleted.id);
      return data;
    },
  );
};

export default defineNuxtPlugin({
  name: 'urql',
  dependsOn: [],
  setup: (nuxtApp) => {
    const ssrHeaders = useRequestHeaders([
      'cookie',
      'user-agent',
      'referer',
      'x-azure-socketip',
      'x-forwarded-for',
      'x-forwarded-host',
      'x-forwarded-port',
      'x-forwarded-proto',
    ]);

    const { apiUrl, origin } = useCurrentCluster();
    const serverEvent = useRequestEvent();
    const { getAppTypeHeaders } = useAppTypeHeaders();
    const lockedRoute = useContentLockedRoute();

    const handleAdminMfaRequired = createAdminMfaRequiredHandler({
      fetchUser: () => useAuthStore().fetchUser(true),
      navigate: fullPath => nuxtApp.runWithContext(() => navigateToAdminMfaLogin(fullPath)),
      currentFullPath: () => nuxtApp._route.fullPath,
    });

    // The fetch options below run synchronously inside urql-vue's subscribe effect, so anything
    // reactive they read becomes a dependency of every in-flight query. Nuxt's route mirror is
    // replaced on each navigation, a query-string change included, so reading it there
    // re-subscribed (and re-fetched) every query the moment a page mirrored a filter into the URL,
    // and the aborted request's error could land after the live result. The path is read through
    // an untracked copy instead.
    const currentPath = createUntrackedMirror(() => nuxtApp._route.path, import.meta.client);

    const _ssrExchange = ssrExchange({
      isClient: import.meta.client,
    });

    // ssr data in nuxt state
    const ssrData = useState<SSRData>(CACHE_KEY);

    // restore SSR state from nuxt payload
    if (import.meta.client) {
      _ssrExchange.restoreData(ssrData.value);
    }

    // when app has rendered in server, send SSR state to client
    if (import.meta.server) {
      nuxtApp.hook('app:rendered', () => {
        ssrData.value = _ssrExchange.extractData();
      });
    }

    const handleOnOperation = (operation: Operation) => {
      if (operation.context.fetchOptions && typeof operation.context.fetchOptions === 'object' && operation.context.fetchOptions.headers) {
        const _headers = <Record<string, string> | undefined>operation.context.fetchOptions.headers;
        if (!_headers || import.meta.client || !serverEvent?.context['guest-id']) {
          return operation;
        }

        if (!_headers.cookie) {
          _headers.cookie = cookie.serialize('guest-id', serverEvent.context['guest-id']);
          return operation;
        }

        const parsedCookie = cookie.parse(_headers.cookie);
        if (!parsedCookie['guest-id']) {
          parsedCookie['guest-id'] = serverEvent.context['guest-id'];

          _headers.cookie = Object.entries(parsedCookie)
            .filter((entry): entry is [string, string] => entry[1] !== undefined)
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join('; ');
        }
      }

      return operation;
    };

    const invalidateUsersList = (cache: {
      inspectFields: (root: string) => Array<{ fieldName: string; fieldKey: string }>;
      invalidate: (root: string, key: string) => void;
    }): void => {
      cache.inspectFields('Query').forEach((field) => {
        if (field.fieldName === 'usersList') {
          cache.invalidate('Query', field.fieldKey);
        }
      });
    };

    const operationGate = createOperationGate();

    const urqlClient = createClient({
      url: `${apiUrl.value}/graphql`,
      exchanges: [
        devtoolsExchange,
        cacheExchange({
          schema,
          keys: graphcacheKeys,
          updates: {
            Mutation: {
              startAnonymousLesson: invalidateViewerProgress,
              upsertLearningAssetCollection: invalidateLearningAssetCollectionLists,
              saveFeedbackSurvey(_result, _args, cache) {
                cache.invalidate('AdminFeedbackSurveyPaginator');
              },
              deleteFeedbackSurvey(_result, _args, cache) {
                cache.invalidate({ __typename: 'AdminFeedbackSurvey', id: (<{ id: string }>_args.input).id });
                cache.invalidate('AdminFeedbackSurveyPaginator');
              },

              upsertUserWalletItemValues(result, _args, cache) {
                // @ts-expect-error urql-internal-logic
                if (result?.upsertUserWalletItemValues?.values) {
                  cache.updateQuery({ query: getSelfWallet }, (data) => {
                    if (!data?.core.user.self?.walletItemValues) {
                      return null;
                    }
                    // @ts-expect-error urql-internal-logic
                    data.core.user.self.walletItemValues = result.upsertUserWalletItemValues.values;
                    return data;
                  });
                }
              },
              createUserWalletContextOutput(result, _args, cache) {
                if (result) {
                  cache.updateQuery({ query: getSelfWallet }, (data) => {
                    if (!data?.core.user.self?.walletContextOutputs) {
                      return null;
                    }
                    // @ts-expect-error urql-internal-logic
                    data.core.user.self.walletContextOutputs = [...result.createUserWalletContextOutput];
                    return data;
                  });
                }
              },
              addUserWalletContexts(result, _args, cache) {
                if (result) {
                  cache.updateQuery({ query: getSelfWallet }, (data) => {
                    if (!data?.core.user.self?.userWalletContexts) {
                      return null;
                    }
                    data.core.user.self.userWalletContexts = [...result.addUserWalletContexts];
                    return data;
                  });
                }
              },
              removeUserWalletContexts(result, _args, cache) {
                if (result) {
                  cache.updateQuery({ query: getSelfWallet }, (data) => {
                    if (!data?.core.user.self?.userWalletContexts) {
                      return null;
                    }
                    data.core.user.self.userWalletContexts = [...result.removeUserWalletContexts];
                    return data;
                  });
                }
              },
              updateStudentRole: viewerScopedCacheUpdater,
              upsertLearningAsset: invalidateLearningAssetLists,
              upsertSpotlight: invalidateSpotlight,
              deleteSpotlight: invalidateSpotlight,
              createLearningAssetFromJson: invalidateLearningAssetLists,
              publishLearningAsset: invalidateLearningAssetLists,
              unpublishLearningAsset: invalidateLearningAssetLists,
              deleteLearningAsset: invalidateDeletedLearningAsset,
              updateTeacherPlaylist(result, _args, cache) {
                // The mutation returns a TeacherPlaylist, but the lessons list is typed
                // LearningAssetCollection — a separate normalised entity for the same record.
                // Invalidate it so the My Lessons grid refetches the updated cover image.
                const id = (<{ updateTeacherPlaylist?: { teacherPlaylist?: { id: string } | null } }>result)
                  ?.updateTeacherPlaylist
                  ?.teacherPlaylist
                  ?.id;
                if (id) {
                  cache.invalidate({ __typename: 'LearningAssetCollection', id });
                }
              },
              // A freshly generated draft is a new LearningAssetCollection that won't be in an
              // already-cached list, so invalidate the myLessonCollections field (the DRAFT list
              // it now belongs to) to refetch its members.
              generateTeacherPlaylist: invalidateMyLessonCollections,
              completeUserProgress(result, _args, cache) {
                cache.invalidate('Query', 'allConsiderationAreas');

                // Completing an activity changes the viewer's in-progress collections, so drop the
                // cached "pick up where you left off" rail — a field on the User self — letting the
                // library subpage catalogue refetch it on next visit instead of serving stale progress.
                const userId = (<{ completeUserProgress?: { query?: { core?: { user?: { self?: { id: string } | null } } } } }>result)
                  ?.completeUserProgress
                  ?.query
                  ?.core
                  ?.user
                  ?.self
                  ?.id;
                if (userId) {
                  const userKey = { __typename: 'User', id: userId };
                  cache.inspectFields(userKey).forEach((field) => {
                    if (field.fieldName === 'inProgressLearningAssetCollections') {
                      cache.invalidate(userKey, field.fieldKey);
                    }
                  });
                }
              },
              deleteLearningAssetCollection: invalidateLearningAssetCollectionLists,
              activateLessonFromTemplate: invalidateMyLessonCollections,
              // Cloning a template mints a new draft LearningAssetCollection that won't be in an
              // already-cached list, so invalidate the myLessonCollections field (the DRAFT list it
              // now belongs to) to refetch its members.
              cloneSourceIntoPlaylist: invalidateMyLessonCollections,
              // Publishing a draft or re-activating an archived lesson moves it between the
              // DRAFT/PUBLISHED/ARCHIVED lists, so invalidate every myLessonCollections field key.
              activateLessonFromPlaylist: invalidateMyLessonCollections,
              updateLessonAudience: invalidateLessonInsightsOnAudienceChange,
              setCurriculumFrameworkStates: invalidateSchoolCurriculumFrameworks,
              // Archiving moves a lesson from the PUBLISHED list to the ARCHIVED list.
              archiveLesson: invalidateMyLessonCollections,
              discardDraftLesson(_result, _args, cache, info) {
                // Drop the deleted draft entity, then refetch the lists it appeared in.
                const playlistId = (<{ input?: { playlistId?: string } }>_args)?.input?.playlistId;
                if (playlistId) {
                  cache.invalidate({ __typename: 'LearningAssetCollection', id: playlistId });
                }
                invalidateMyLessonCollections(_result, _args, cache, info);
              },
              requestReferralPayout(result, _args, cache) {
                if (result?.requestReferralPayout) {
                  // @ts-expect-error urql-internal-logic
                  cache.updateQuery({ query: fetchReferralsData }, (data) => {
                    return {
                      ...data,
                      getReferralBalance: result.requestReferralPayout,
                    };
                  });
                }
              },
              payoutReferralRedemption(_result, _args, cache) {
                cache.inspectFields('Query').forEach((field) => {
                  if (field.fieldName === 'referralRedemptions') {
                    cache.invalidate('Query', field.fieldKey);
                  }
                });
              },
              disableReferralCode(_result, _args, cache) {
                cache.inspectFields('Query').forEach((field) => {
                  if (field.fieldName === 'referralRedemptions') {
                    cache.invalidate('Query', field.fieldKey);
                  }
                });
              },
              enableReferralCode(_result, _args, cache) {
                cache.inspectFields('Query').forEach((field) => {
                  if (field.fieldName === 'referralRedemptions') {
                    cache.invalidate('Query', field.fieldKey);
                  }
                });
              },
              awardExternalCoins(result, _args, cache) {
                const award = (<AwardExternalCoinsMutation>result).awardExternalCoins;
                if (!award || award.dryRun) {
                  return;
                }

                // A real run moves each awarded user's balance and ledger, so the admin user page
                // refetches them rather than showing the pre-award numbers. The page's identity
                // card reads the balance off AdminUserDetail, a separate entity from the ledger's
                // AdminUser, so both are dropped.
                award.entries
                  .flatMap(entry => (entry.outcome === ExternalCoinAwardOutcome.Awarded && entry.userId ? [entry.userId] : []))
                  .forEach((userId) => {
                    const userKey = { __typename: 'AdminUser', id: userId };
                    cache.inspectFields(userKey).forEach((field) => {
                      if (field.fieldName === 'latestCoinBalance' || field.fieldName === 'coinsTransactions') {
                        cache.invalidate(userKey, field.fieldKey);
                      }
                    });
                    cache.invalidate({ __typename: 'AdminUserDetail', id: userId }, 'latestCoinBalance');
                  });
              },
              approveCashout(_result, _args, cache) {
                cache.inspectFields('Query').forEach((field) => {
                  if (field.fieldName === 'cashoutsList') {
                    cache.invalidate('Query', field.fieldKey);
                  }
                });
              },
              bulkUpdateUserJunkStatus(_result, _args, cache) {
                cache.inspectFields('Query').forEach((field) => {
                  if (field.fieldName === 'cashoutsList') {
                    cache.invalidate('Query', field.fieldKey);
                  }
                });
              },
              updateUserJunkStatus(_result, _args, cache) {
                cache.inspectFields('Query').forEach((field) => {
                  if (field.fieldName === 'referralRedemptions') {
                    cache.invalidate('Query', field.fieldKey);
                  }
                });
                cache.inspectFields('Query').forEach((field) => {
                  if (field.fieldName === 'cashoutsList') {
                    cache.invalidate('Query', field.fieldKey);
                  }
                });
                invalidateUsersList(cache);
              },
              bulkUpdateUserJunkStatusForReferralRedemptions(_result, _args, cache) {
                cache.inspectFields('Query').forEach((field) => {
                  if (field.fieldName === 'referralRedemptions') {
                    cache.invalidate('Query', field.fieldKey);
                  }
                });
              },
              bulkUpdateUserJunkStatusForRedeemableCoins(_result, _args, cache) {
                invalidateUsersList(cache);
              },
              updateUserAppealVerification(result, args, cache) {
                // The mutation payload updates the normalized User entity (tier, score, signals),
                // but the user page's actions menu reads appealVerifiedAt off the UserDetail DTO —
                // a separate cache entity graphcache cannot link to the payload. Patch the cached
                // getUserDetail for this user directly from the result; no refetch needed.
                const appealVerifiedAt = (<{ updateUserAppealVerification?: { appealVerifiedAt?: string | null } }>result)
                  ?.updateUserAppealVerification
                  ?.appealVerifiedAt ?? null;
                cache.updateQuery(
                  { query: getUserDetail, variables: { userId: <string>args.userId } },
                  (data) => {
                    if (!data?.core.user.userDetail) {
                      return data;
                    }
                    data.core.user.userDetail.appealVerifiedAt = appealVerifiedAt;
                    return data;
                  },
                );
              },
              createCoachSessionAnnotation: appendCreatedCoachSessionAnnotation,
              deleteCoachSessionAnnotation: removeDeletedCoachSessionAnnotation,
              createBadge(_result, _args, cache) {
                cache.invalidate('AdminBadgePaginator');
              },
              deleteBadge(_result, _args, cache) {
                cache.invalidate('AdminBadgePaginator');
              },
              upsertPartner(_result, _args, cache) {
                cache.invalidate('AdminPartnerPaginator');
              },
              deletePartner(_result, _args, cache) {
                cache.invalidate('AdminPartnerPaginator');
              },
              bulkProcessCashouts(_result, _args, cache) {
                cache.invalidate('AdminCashoutPaginator');
              },
              bulkApproveCashouts(_result, _args, cache) {
                cache.invalidate('AdminCashoutPaginator');
              },
              bulkJumpQueueCashouts(_result, _args, cache) {
                cache.invalidate('AdminCashoutPaginator');
                // Jumping the queue creates the batch the rows move into, so the batches list is
                // stale too.
                cache.invalidate('AdminRedemptionBatchPaginator');
              },
              deleteConsiderationArea: invalidateConsiderationAreaLists,
              createConsiderationArea: invalidateConsiderationAreaLists,
              updateConsiderationArea: invalidateConsiderationAreaLists,
            },
            SchoolMutations: {
              declineStudentRoleRequest(_result, _args, cache) {
                cache.invalidate('StudentRoleRequest');
                cache.invalidate('StudentRoleRequestPaginator');
              },
              inviteStudents(_result, _args, cache) {
                cache.invalidate('StudentRoleInvite');
                cache.invalidate('StudentRoleInvitePaginator');
              },
              deleteStudentRoleInvite(_result, _args, cache) {
                cache.invalidate('StudentRoleInvite');
                cache.invalidate('StudentRoleInvitePaginator');
              },
              bulkDeclineStudentRoleRequests(_result: SchoolMutations, _args: BulkDeclineStudentRoleRequestsInput, cache) {
                if (_result.bulkDeclineStudentRoleRequests.success === true) {
                  cache.invalidate('StudentRoleRequest');
                  cache.invalidate('StudentRoleRequestPaginator');
                }
              },
              bulkResendStudentRoleInvites(_result: SchoolMutations, _args, cache) {
                if (_result.bulkResendStudentRoleInvites.success === true) {
                  cache.invalidate('StudentRoleInvite');
                  cache.invalidate('StudentRoleInvitePaginator');
                }
              },
              bulkDeleteStudentRoleInvites(_result: SchoolMutations, _args, cache) {
                if (_result.bulkDeleteStudentRoleInvites.success === true) {
                  cache.invalidate('StudentRoleInvite');
                  cache.invalidate('StudentRoleInvitePaginator');
                }
              },
              bulkDeleteStudentRoles(_result: SchoolMutations, _args, cache) {
                if (_result.bulkDeleteStudentRoles.success === true) {
                  cache.invalidate('StudentRolePaginator');
                }
              },
            },
            AuthMutations: {
              logout(_result, _args, cache) {
                // invalidate caches for all query kinds
                cache.inspectFields('Query').forEach((field) => {
                  cache.invalidate('Query', field.fieldKey);
                });
              },
              verifyLoginCode: viewerScopedCacheUpdater,
              exchangeSocialLogin: viewerScopedCacheUpdater,
              loginWithPassword: viewerScopedCacheUpdater,
              confirmAdminMfaEnrolment: viewerScopedCacheUpdater,
              verifyAdminMfa: viewerScopedCacheUpdater,
            },
            UserMutations: {
              signUpStudent: viewerScopedCacheUpdater,
            },
            Subscription: {
              studentRoleRequestCreated(_result, _args, cache) {
                // clean paginator cache when event is being captured outside manage-student-invites
                // manage-student-invites has a new-data notification banner
                if (nuxtApp._route.name !== 'manage-student-invites') {
                  cache.invalidate('StudentRoleRequestPaginator');
                }
              },
              studentRoleInviteUpdated(_result, _args, cache) {
                // handle for studentInvite accepted , warning the user if invite has been accepted / declined
                // and refresh the user table or refresh the user invite table
                const updated = _result?.studentRoleInviteUpdated;
                if (!updated) {
                  return;
                }

                if ((<SchoolStudentRoleInviteFieldsFragment>updated).status === RoleInviteStatus.Accepted) {
                  cache.invalidate('StudentRolePaginator');
                  cache.invalidate('StudentRoleRequestPaginator');
                }
              },
            },
          },
        }),
        _ssrExchange,
        mapExchange({
          onOperation: handleOnOperation,
          onResult: (result) => {
            if (import.meta.server) {
              return result;
            }

            const { reset, refreshExpiry } = useAuthStore();

            // Streamed responses do not bump Laravel session cookie expiry, only the initial request, such as with a subscription, so we avoid bumping the expiry tracking on the FE
            if (!result.hasNext) {
              refreshExpiry();
              GQL_OP_STREAMING_SET.delete(result.operation.key);
            }
            else if (!GQL_OP_STREAMING_SET.has(result.operation.key)) {
              refreshExpiry();
              GQL_OP_STREAMING_SET.add(result.operation.key);
            }

            if (!result.error) {
              return result;
            }

            const unauthenticatedError = findGraphqlErrorWithCode(result.error, 'UNAUTHENTICATED');

            if (unauthenticatedError && !result.operation.context.answersUnauthenticated) {
              const { logoutRedirectRoute } = usePlatformAppConfig(useRoute().path);
              reset();
              nuxtApp.$analytics?.reset();
              navigateTo(`${clusterUrl(logoutRedirectRoute)}?sessionExpired=true`, { replace: true, external: true });
              return result;
            }

            if (findGraphqlErrorWithCode(result.error, 'MFA_REQUIRED')) {
              void handleAdminMfaRequired();
              return result;
            }

            // Only the capability directive refuses with MISSING_CAPABILITY; every other policy
            // denial keeps the shared UNAUTHORIZED code, so a page the viewer legitimately holds
            // is never taken over by a lock claiming they lack its capabilities.
            const missingCapabilityError = findGraphqlErrorWithCode(result.error, 'MISSING_CAPABILITY');

            if (missingCapabilityError) {
              // On a capability-gated route the locked takeover is the denial UX: it keeps the
              // admin shell and its nav, so hand the denial to the shared state the route gate
              // writes instead of replacing the page with a bare error. The gate runs on every
              // navigation and clears the state, so the next allowed page renders normally.
              // A refusal the route does not gate on is a side query reaching into another area
              // from a page the viewer legitimately holds: the page stays and the denial is named
              // in a toast instead (see resolveCapabilityDenial).
              const deniedRoute = resolveCapabilityDenial(
                nuxtApp._route,
                missingCapabilityError.extensions.capability,
              );

              if (deniedRoute) {
                lockedRoute.value = deniedRoute;
              }

              return result;
            }

            const forbiddenError = findGraphqlErrorWithCode(result.error, 'UNAUTHORIZED');

            if (forbiddenError) {
              showError({ statusCode: 403, statusMessage: <string>forbiddenError.extensions.clientMessage });
              return result;
            }

            return result;
          },
        }),
        operationGate.exchange,
        fetchExchange,
        ...(import.meta.client
          ? [subscriptionExchange({ forwardSubscription: useSubscriptionExchange().forwardSubscriptionToPusher })]
          : [subscriptionExchange({
              forwardSubscription: () => ({
                subscribe: ({ complete }) => {
                  // SSR: immediately complete subscriptions (they're client-only)
                  complete();
                  return { unsubscribe: () => {} };
                },
              }),
            })]),
      ],
      fetchOptions: () => {
        const appTypeHeaders = getAppTypeHeaders(currentPath());

        // Sanctum's stateful check matches the request's Referer (preferred),
        // then Origin, host against `sanctum.stateful`. Two SSR gotchas: browsers
        // don't always send Origin on same-origin GETs (typed URLs, bookmarks,
        // reloads), and after an OAuth redirect the SSR document still carries
        // `Referer: accounts.google.com` — the callback→dashboard hop is a
        // client-side navigation, so the referrer never resets. So on SSR pin
        // BOTH Referer and Origin to the cluster origin; otherwise the forwarded
        // cross-site referer wins, auth falls through to token auth, `self`
        // returns Unauthenticated, and the user is bounced to the guest home.
        return {
          headers: import.meta.server
            ? { ...ssrHeaders, ...appTypeHeaders, origin: origin.value, referer: origin.value }
            : { ...ssrHeaders, ...appTypeHeaders },
        };
      },
    });

    nuxtApp.vueApp.provide('$urql', ref(urqlClient));
    nuxtApp.provide('urql', urqlClient);

    return { provide: { urqlClient, operationGate } };
  },
});

declare module '#app' {
  interface NuxtApp {
    $urqlClient: Client;
    $operationGate: OperationGate;
  }
}
